/**
 * Turns the brand's quoted delivery window into dates.
 *
 * The window itself is real — read from the shipping rate the customer will see
 * again at checkout — and it does not vary by destination, so there is nothing
 * to ask the shopper and nothing to compute from where they live. All this does
 * is count business days forward, which needs today's date and therefore has to
 * happen in the browser rather than in a cached Liquid render.
 *
 * The block already prints "Arrives 5–7 business days" server-side. This
 * upgrades that to real dates; with scripting off the useful fact still stands.
 */

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const isWeekend = (d) => d.getDay() === 0 || d.getDay() === 6;

/** Business days only — no carrier delivers a parcel on Sunday. */
function addBusinessDays(from, days) {
  const date = new Date(from.getTime());
  let left = days;
  while (left > 0) {
    date.setDate(date.getDate() + 1);
    if (!isWeekend(date)) left -= 1;
  }
  return date;
}

/**
 * Where the count starts. An order placed after the cutoff, or on a weekend,
 * is not going anywhere until the next working day, and an estimate that
 * pretends otherwise is wrong by a weekend every Saturday.
 */
function startOfCount(now, cutoffHour) {
  const start = new Date(now.getTime());
  if (isWeekend(start) || start.getHours() >= cutoffHour) {
    do {
      start.setDate(start.getDate() + 1);
    } while (isWeekend(start));
  }
  return start;
}

/** "Tue 16 Sep" — the weekday matters more than the year for a delivery date. */
function format(date, withYear) {
  const opts = { weekday: 'short', day: 'numeric', month: 'short' };
  if (withYear) opts.year = 'numeric';
  return new Intl.DateTimeFormat('en-CA', opts).format(date);
}

class BunchberryDelivery extends HTMLElement {
  connectedCallback() {
    const min = parseInt(this.dataset.min, 10);
    const max = parseInt(this.dataset.max, 10);
    if (!Number.isFinite(min) || !Number.isFinite(max) || max <= 0) return;

    const out = this.querySelector('[data-eta-out]');
    if (!out) return;

    const now = new Date();
    const cutoff = parseInt(this.dataset.cutoff, 10) || 13;
    const start = startOfCount(now, cutoff);

    const earliest = addBusinessDays(start, min);
    const latest = addBusinessDays(start, max);

    // A range crossing into next year needs the year to make sense; within the
    // next few weeks it is noise.
    const spansYear = earliest.getFullYear() !== now.getFullYear();
    const label = this.dataset.label || 'Arrives';

    const text =
      min === max
        ? `${label} ${format(latest, spansYear)}`
        : `${label} ${format(earliest, spansYear)} – ${format(latest, spansYear)}`;

    out.innerHTML = '';
    const strong = document.createElement('strong');
    strong.textContent = text;
    out.append(strong);
  }
}

if (!customElements.get('bunchberry-delivery')) {
  customElements.define('bunchberry-delivery', BunchberryDelivery);
}
